import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { PersonData } from "@/entities/people/types";
import DetailFields, {
  DetailFieldsContext
} from "@/components/layouts/DetailFields";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { useEntityDetailFieldsLayout } from "@/hooks/useEntityFieldsConfig";
import CreateNewOptionModal from "@/components/controls/CreateNewOptionModal";
import { useEntityFields, useUpdateEntityField } from "@/pages/settings/hooks";
import { get } from "lodash";
import { getParsedFieldValue } from "@/utils/entityFieldsUtils/shared";

interface Props {
  person?: PersonData;
  onUpdateField: (
    field: string,
    newValue: string | number | boolean | string[]
  ) => void;
  className?: string;
  containerClassName?: string;
  isDrawerOpen?: boolean;
  isQuickView?: boolean;
}

const PersonDetailFields: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [showCreateOptionModal, setShowCreateOptionModal] = useState(false);
  const [newOptionName, setNewOptionName] = useState("");
  const [currentFieldForNewOption, setCurrentFieldForNewOption] = useState<{
    fieldId: string;
    fieldKey: string;
  } | null>(null);

  const { data: entityFieldsResponse } = useEntityFields(EntityType.PERSON);
  const updateEntityField = useUpdateEntityField(EntityType.PERSON);

  const handleCreateNewOption = async (optionName: string) => {
    if (!currentFieldForNewOption) return;

    try {
      const currentField = entityFieldsResponse?.fields?.find(
        (field) => field.id === currentFieldForNewOption.fieldId
      );

      if (!currentField?.config?.options) return;

      const currentOptions = currentField.config.options;
      const optionsArray = Array.isArray(currentOptions)
        ? currentOptions
        : Object.keys(currentOptions);

      const updatedOptionsArray = [...optionsArray, optionName];

      const updatedOptions = Array.isArray(currentOptions)
        ? updatedOptionsArray
        : updatedOptionsArray.reduce(
            (acc, option) => {
              acc[option] = option;
              return acc;
            },
            {} as Record<string, string>
          );

      await updateEntityField.mutateAsync({
        data: {
          id: currentFieldForNewOption.fieldId,
          config: {
            ...currentField.config,
            options: updatedOptions,
            allowUsersToAdd: true
          }
        },
        pathParams: {
          entityType: "people",
          fieldId: currentFieldForNewOption.fieldId
        }
      });

      // Auto-select the newly created option
      if (props.onUpdateField) {
        const isMultiSelect = currentField.displayType === "multiSelect";

        if (isMultiSelect) {
          const fieldConfig = entityFieldsResponse?.fields?.find(
            (f) => f.id === currentFieldForNewOption.fieldId
          )?.config;
          const rawValue = get(props.person, currentFieldForNewOption.fieldKey);

          const currentValues: string[] = getParsedFieldValue(rawValue, {
            type: "multiSelect",
            ...fieldConfig
          });
          const newValues = Array.isArray(currentValues)
            ? [...currentValues, optionName]
            : [optionName];
          props.onUpdateField(currentFieldForNewOption.fieldKey, newValues);
        } else {
          props.onUpdateField(currentFieldForNewOption.fieldKey, optionName);
        }
      }

      setShowCreateOptionModal(false);
      setNewOptionName("");
      setCurrentFieldForNewOption(null);
    } catch (error) {
      console.error("Failed to create new option:", error);
    }
  };

  const context: DetailFieldsContext = {
    showCreateOptionModal,
    newOptionName,
    currentFieldForNewOption,
    setShowCreateOptionModal,
    setNewOptionName,
    setCurrentFieldForNewOption,
    handleCreateNewOption,
    onUpdateField: props.onUpdateField
  };

  const { fieldsConfig, sections } = useEntityDetailFieldsLayout(
    EntityType.PERSON,
    props.person,
    context,
    { groupBySection: !props.isQuickView }
  );

  return (
    <>
      <DetailFields
        entity={props.person}
        fieldsConfig={fieldsConfig}
        sections={sections}
        onUpdateField={props.onUpdateField}
        className={props.className}
        containerClassName={props.containerClassName}
        tPrefix="people.personView"
        isDrawerOpen={props.isDrawerOpen}
        isQuickView={props.isQuickView}
        context={context}
      />
      <CreateNewOptionModal
        isOpen={showCreateOptionModal}
        onClose={() => {
          setShowCreateOptionModal(false);
          setNewOptionName("");
          setCurrentFieldForNewOption(null);
        }}
        onCreateOption={context.handleCreateNewOption || (async () => {})}
        title={t("createNewOption.title", { optionName: newOptionName })}
        description={t("createNewOption.description")}
        createButtonText={t("createNewOption.create")}
        cancelButtonText={t("cancel")}
        optionName={newOptionName}
      />
    </>
  );
};

export default PersonDetailFields;
